import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { tokens } from '../../theme/tokens';

/**
 * ConfirmDialog Component
 * 
 * A reusable confirmation dialog for destructive or important actions.
 * Follows WCAG 2.1 AA guidelines with proper focus management and keyboard support.
 * 
 * @component
 * @example
 * ```jsx
 * <ConfirmDialog
 *   open={open}
 *   title="Delete User"
 *   message="Are you sure you want to delete this user? This action cannot be undone."
 *   onConfirm={handleDelete}
 *   onCancel={handleCancel}
 *   severity="error"
 * />
 * ```
 */
const ConfirmDialog = ({
  open,
  title,
  message,
  children,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  severity = 'warning',
  loading = false,
  maxWidth = 'sm',
}) => {
  const theme = useTheme();
  const confirmButtonRef = useRef(null);

  // Focus management - focus confirm button on open
  useEffect(() => {
    if (open && confirmButtonRef.current) {
      // Small delay to ensure dialog is rendered
      const timer = setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Handle keyboard events
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !loading) {
      event.preventDefault();
      onConfirm();
    } else if (event.key === 'Escape' && !loading) {
      event.preventDefault();
      onCancel();
    }
  };

  // Get severity-specific styling
  const getSeverityConfig = () => {
    switch (severity) {
      case 'error':
        return {
          icon: ErrorOutlineIcon,
          color: theme.palette.error.main,
          confirmButtonColor: 'error',
        };
      case 'warning':
        return {
          icon: WarningAmberIcon,
          color: theme.palette.warning.main,
          confirmButtonColor: 'warning',
        };
      case 'info':
      default:
        return {
          icon: InfoIcon,
          color: theme.palette.info.main,
          confirmButtonColor: 'primary',
        };
    }
  };

  const severityConfig = getSeverityConfig();
  const Icon = severityConfig.icon;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth={maxWidth}
      fullWidth
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      onKeyDown={handleKeyDown}
      PaperProps={{
        sx: {
          borderRadius: `${tokens.radius.lg}px`,
          boxShadow: tokens.shadow.xl,
        },
      }}
    >
      <DialogTitle
        id="confirm-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space.x2,
          pb: tokens.space.x2,
          pt: tokens.space.x3,
          px: tokens.space.x3,
        }}
      >
        <Icon
          sx={{
            color: severityConfig.color,
            fontSize: 28,
          }}
          aria-hidden="true"
        />
        {title}
      </DialogTitle>

      <DialogContent
        sx={{
          px: tokens.space.x3,
          py: tokens.space.x2,
        }}
      >
        {message && (
          <DialogContentText
            id="confirm-dialog-description"
            sx={{
              color: theme.palette.text.primary,
              fontSize: tokens.fontSize.sm,
              lineHeight: tokens.lineHeight.relaxed,
            }}
          >
            {message}
          </DialogContentText>
        )}
        {children}
      </DialogContent>

      <DialogActions
        sx={{
          px: tokens.space.x3,
          py: tokens.space.x3,
          gap: tokens.space.x2,
        }}
      >
        <Button
          onClick={onCancel}
          variant="outlined"
          disabled={loading}
          sx={{
            minWidth: 100,
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          ref={confirmButtonRef}
          onClick={onConfirm}
          variant="contained"
          color={severityConfig.confirmButtonColor}
          disabled={loading}
          autoFocus
          sx={{
            minWidth: 100,
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
            '&.Mui-disabled': {
              backgroundColor: alpha(theme.palette.action.disabled, 0.12),
            },
          }}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConfirmDialog.propTypes = {
  /** Whether the dialog is open */
  open: PropTypes.bool.isRequired,
  /** Dialog title */
  title: PropTypes.string.isRequired,
  /** Dialog message (can be used instead of children for simple text) */
  message: PropTypes.string,
  /** Custom content (can be used instead of message for complex content) */
  children: PropTypes.node,
  /** Callback when confirm button is clicked */
  onConfirm: PropTypes.func.isRequired,
  /** Callback when cancel button is clicked or dialog is closed */
  onCancel: PropTypes.func.isRequired,
  /** Text for confirm button */
  confirmText: PropTypes.string,
  /** Text for cancel button */
  cancelText: PropTypes.string,
  /** Severity level affecting icon and button color */
  severity: PropTypes.oneOf(['info', 'warning', 'error']),
  /** Whether an action is in progress */
  loading: PropTypes.bool,
  /** Maximum width of the dialog */
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
};

export default ConfirmDialog;
